import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import type { FilterOption } from '@/types/candidate';

interface StoreAutocompleteProps {
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
}

const StoreAutocomplete = ({ options, value, onChange }: StoreAutocompleteProps) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('ru-RU');
    if (!normalizedSearch) return options;
    return options.filter((option) =>
      option.label.toLocaleLowerCase('ru-RU').includes(normalizedSearch),
    );
  }, [options, search]);

  useOnClickOutside(rootRef, () => setOpen(false), open);

  const toggleOption = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((selectedValue) => selectedValue !== optionValue)
        : [...value, optionValue],
    );
    setSearch('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (event.key === 'Enter' && open && filteredOptions.length > 0) {
      event.preventDefault();
      toggleOption(filteredOptions[0].value);
    }
  };

  return (
    <div ref={rootRef} className="relative mt-1">
      <div
        className={`flex h-[42px] items-center rounded border bg-white px-3 ${
          open ? 'border-[#631d76]' : 'border-gray-300'
        }`}
      >
        <input
          id="candidate-store-search"
          type="search"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="candidate-store-options"
          aria-expanded={open}
          autoComplete="off"
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={value.length ? `Выбрано: ${value.length}` : 'Начните вводить магазин'}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
        />
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="ml-2 text-lg leading-none text-gray-400 hover:text-[#631d76]"
            aria-label="Очистить выбранные магазины"
            title="Очистить"
          >
            ×
          </button>
        )}
        <span className="ml-2 text-xs text-gray-400" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && (
        <div
          id="candidate-store-options"
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-30 mt-1 max-h-64 w-full min-w-[320px] overflow-y-auto rounded border border-gray-200 bg-white py-1 shadow-lg"
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm font-normal text-gray-500">Магазины не найдены</div>
          ) : (
            filteredOptions.map((option) => {
              const checked = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggleOption(option.value)}
                  className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm font-normal hover:bg-purple-50 ${
                    checked ? 'bg-purple-50 text-[#631d76]' : 'text-gray-700'
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-xs ${
                      checked ? 'border-[#631d76] bg-[#631d76] text-white' : 'border-gray-300'
                    }`}
                    aria-hidden="true"
                  >
                    {checked ? '✓' : ''}
                  </span>
                  <span>{option.label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default StoreAutocomplete;
